import { useState } from 'react';
import { AlertColor } from '@mui/material';

/**
 * Snackbar state, in its own module rather than in the `hooks` barrel.
 *
 * The barrel (`hooks/index.tsx`) imports `authService`, which reaches
 * `utils/index.jsx` and from there `jose` — pure ESM that Jest cannot transform, so
 * any test whose component imports the barrel dies at load with
 * "Unexpected token 'export'" before a single assertion runs. Importing this file
 * directly keeps that graph out of the test.
 *
 * The barrel still re-exports both names, so existing callers are untouched.
 *
 * `severity` is typed `AlertColor` and not the `Color` the barrel used to import from
 * `@mui/lab`: this version of the package does not export that name, so the old import
 * was already a type error sitting in the baseline. It is the same four-value union,
 * under the name MUI v5 actually exports.
 */
export interface SnackbarType {
    show: boolean;
    message?: string | undefined;
    severity?: AlertColor | undefined;
}

export function useSnackBarState() {
    const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({ show: false });

    function handleCloseSnackbar() {
        setShowSnackbar({ show: false });
    }

    return [showSnackbar, setShowSnackbar, handleCloseSnackbar] as const;
}
